import { makeModuleInstance } from './makeModuleInstance';

/**
 * Create an _app instance_ of type `T`: exactly one instance per app, surviving that app's own
 * module-graph fragmentation, regardless of how many times this function runs.
 *
 * On the server, a Next.js app's instrumentation entrypoint, layouts, and route handlers each get
 * their own copy of a package's module-scope state, so a plain module-scope variable
 * (see {@link makeModuleInstance}) silently resets per graph. To survive that, this falls back to
 * a `globalThis` slot, which is one object per process - safe there because each server process
 * hosts exactly one app.
 *
 * In a browser, that fragmentation doesn't happen - an app's client bundle has a single module
 * registry, so plain module scope already gives one instance for the whole app. Using `globalThis`
 * there would be actively wrong: `globalThis` is `window`, shared by every independently-bundled
 * `@zg-rentals` bundle on the page, including federated micro-frontends, so it would leak this
 * instance into unrelated apps sharing the page instead of keeping it scoped to one. So in the
 * browser this uses module scope instead, same as {@link makeModuleInstance}.
 *
 * Use this for state that must be configured once (typically during app bootstrap) and read
 * correctly from anywhere in that one app.
 *
 * @param initializer builds the initial value; called only once per app, so it is safe to pass a
 *  constructor with side effects
 * @param key a string that uniquely identifies this instance across the whole app. Only consulted
 *  on the server, where it is what lets separate module graphs find the same `globalThis` slot.
 * @returns a `get` function and `set` function
 */
export function makeAppInstance<T>(initializer: () => T, key: string) {
  if (typeof window === 'undefined') {
    const slot = Symbol.for(key);
    const globalScope = globalThis as Record<symbol, T>;

    if (!(slot in globalScope)) {
      globalScope[slot] = initializer();
    }

    return {
      get: () => globalScope[slot],

      set(newInstance: T) {
        globalScope[slot] = newInstance;
        return newInstance;
      },
    };
  }

  return makeModuleInstance(initializer());
}
